import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useLeadContext } from "../../contexts/LeadContext";
import { leadsService } from "../../services/leads.service";
import { customFieldsService } from "../../services/customFields.service";
import { companiesService } from "../../services/companies.service";
import { contactsService } from "../../services/contacts.service";
import { extractApiErrorMessage } from "../../utils/apiError";
import { AddCustomFieldValueForm } from "../../ui/forms/CustomFieldForms";
import LeadCommunicationsPanel from "./communications/LeadCommunicationsPanel";
import CommunicationDetailPanel from "./communications/CommunicationDetailPanel";
import ConversationSummaryPanel from "./communications/ConversationSummaryPanel";
import LoadingScreen from "../../ui/loadingScreen/LoadingScreen";
import ErrorPopup from "../../ui/Modals/ErrorPopup";
import { ConfirmDeleteModal } from "../database/ContactConfirmModals";
import { describeRequestError } from "../../utils/requestErrorMessage";

import styles from "../../styles/components/LeadInfo.module.css";
import ModalOverlay from "../../ui/Modals/ModalOverlay";

import LeadStageCard from "./LeadStageCard";
import LeadDetailsCard from "./LeadDetailsCard";
import CompanyDetailModal from "../database/CompanyDetailModal";
import LeadStatusProgress from "./LeadStatusProgress";
import LeadChannelTabs, { CHANNEL_MAIL, CHANNEL_WHATSAPP } from "./whatsapp/LeadChannelTabs";
import LeadWhatsAppPanels from "./whatsapp/LeadWhatsAppPanels";
import { useWhatsAppAvailability } from "./whatsapp/useWhatsAppAvailability";
import useWhatsAppEnabled from "../../hooks/useWhatsAppEnabled";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";

const buildEmptyLead = () => ({
  id: null,
  cliente_id: "",
  lead_name: "",
  person_ref: null,
  type: null,
  account: null,
  country: null,
  responsible: null,
  status: null,
  related_persons: [],
  recipients: [],
  persons: [],
  company: null,
  company_name: "",
});

const buildLeadState = (lead) => ({
  id: lead?.id ?? null,
  cliente_id: lead?.cliente_id ?? "",
  lead_name: lead?.lead_name ?? "",
  person_ref: lead?.person_ref ?? null,
  type: lead?.type ?? null,
  account: lead?.account ?? null,
  country: lead?.country ?? null,
  responsible: lead?.responsible ?? null,
  status: lead?.status ?? null,
  related_persons: lead?.related_persons ?? [],
  recipients: lead?.recipients ?? [],
  persons: lead?.persons ?? [],
  company: lead?.company ?? null,
  company_name: lead?.company_name ?? "",
});

const buildPatchPayload = (field, value) => {
  if (["type", "account", "responsible", "status", "person_ref"].includes(field)) {
    return {
      [field]: value?.id ? { id: value.id } : null,
    };
  }

  if (field === "lead_name") {
    return { lead_name: value ?? "" };
  }

  if (field === "company") {
    return {
      company: value?.id ?? null,
    };
  }

  if (field === "related_persons") {
    return {
      related_persons: (value || []).map((person) => ({ id: person.id })),
    };
  }

  if (field === "recipients") {
    return {
      recipients: (value || []).map((person) => ({ id: person.id })),
    };
  }

  return {
    [field]: value ?? "",
  };
};

const getCompanyId = (company) =>
  (typeof company === "object" ? company?.id : company) ?? null;

const getPersonName = (person = {}) =>
  [person.first_name, person.middle_name, person.last_name]
    .filter(Boolean)
    .join(" ");

// El listado de personas trae emails/teléfonos como arrays (emails[], contacts[]),
// pero el form los muestra como campos sueltos. Estos helpers derivan el valor
// principal para poder autocompletar al elegir un receptor.
const getActivePersonEmail = (person = {}) => {
  if (person.email) return person.email;
  const emails = Array.isArray(person.emails) ? person.emails : [];
  const activeEmail = emails.find(
    (email) => email?.active !== false && email?.data,
  );
  return activeEmail?.data || emails[0]?.data || "";
};

const getPrimaryPersonPhone = (person = {}) => {
  if (person.mobile_phone) return person.mobile_phone;
  const phones = Array.isArray(person.contacts) ? person.contacts : [];
  return phones.find((phone) => phone?.data)?.data || "";
};

const getPersonPhoto = (person = {}) =>
  person.user_photo || person.photo || person.avatar || person.image || null;

const PERSON_PAGE_SIZE = 50;

const getPersonPickerEmptyMessage = ({ accountId, companyId }) => {
  if (!accountId) return "Elegí una cuenta para buscar personas.";
  if (!companyId) return "Elegí una compañía para buscar personas.";
  return undefined;
};

const toPersonOption = (person = {}) => ({
  ...person,
  name: person.name || getPersonName(person),
  position: person.position || "",
  email: getActivePersonEmail(person),
  mobile_phone: getPrimaryPersonPhone(person),
  photo: getPersonPhoto(person),
});

const buildLeadPersons = (recipient, relatedPersons = [], recipients = []) => {
  const recipientIds = new Set(
    (recipients || []).map((person) => Number(person?.id)).filter(Boolean),
  );
  if (recipient?.id) recipientIds.add(Number(recipient.id));

  const persons = [];
  const seenIds = new Set();
  const pushPerson = (person) => {
    if (!person?.id || seenIds.has(Number(person.id))) return;
    seenIds.add(Number(person.id));
    persons.push({
      id: person.id,
      name: person.name || getPersonName(person),
      position: person.position || "",
      email: getActivePersonEmail(person),
      mobilePhone: getPrimaryPersonPhone(person),
      photo: getPersonPhoto(person),
      role: recipientIds.has(Number(person.id)) ? "receptor" : "contacto",
    });
  };

  if (recipient?.id) pushPerson(recipient);
  (recipients || []).forEach(pushPerson);
  (relatedPersons || []).forEach(pushPerson);

  return persons;
};

const buildRecipientFromPersonRef = (personRef = {}) => ({
  id: personRef.id,
  first_name: personRef.first_name || "",
  middle_name: personRef.middle_name || "",
  last_name: personRef.last_name || "",
  position: personRef.position || "",
  email: getActivePersonEmail(personRef),
  mobile_phone: getPrimaryPersonPhone(personRef),
  photo: getPersonPhoto(personRef),
  name: personRef.name || getPersonName(personRef),
});

const countryFromCompany = (company, countries, currentCountry) => {
  if (!company || !("country" in company)) return currentCountry;

  const countryId =
    company.country && typeof company.country === "object"
      ? company.country.id
      : company.country;
  if (countryId == null) return null;
  if (Number(currentCountry?.id) === Number(countryId)) return currentCountry;

  return (
    (countries || []).find((country) => Number(country.id) === Number(countryId)) ??
    null
  );
};

const applyFieldToLead = (lead, field, value) => {
  if (field === "person_ref") {
    return {
      ...lead,
      person_ref: value,
    };
  }

  return {
    ...lead,
    [field]: value,
  };
};

const LeadInfoModal = ({
  visible,
  onClose,
  lead,
  statuses,
  mode = "edit",
  onSaved,
  onDeleted,
  initialSubjectGroupId = null,
  onUnreadMailChange,
  initialWhatsAppThreadId = null,
}) => {
  const isCreate = mode === "create";

  const {
    types,
    countries,
    accounts,
    responsible,
    customFields,
    customFieldsLoading,
    fetchCustomFieldsByAccount,

    leadCustomFieldValues,
    setLeadCustomFieldValues,
    leadCustomFieldValuesLoading,
    fetchLeadCustomFieldValues,

    fetchCompanyDetail,

    personsByCompany,
    fetchPersonsByCompany,

    selectedCommunicationSubject,
    supervisedAccounts,
  } = useLeadContext();

  const [editableLead, setEditableLead] = useState(
    lead ? buildLeadState(lead) : buildEmptyLead(),
  );

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [pendingStatusId, setPendingStatusId] = useState(null);
  const [showCustomFieldForm, setShowCustomFieldForm] = useState(false);
  const leadId = lead?.id ?? null;
  const handleUnreadChange = useCallback(
    (hasUnread) => {
      if (!leadId) return;
      onUnreadMailChange?.(leadId, hasUnread);
    },
    [leadId, onUnreadMailChange],
  );
  const [composerMode, setComposerMode] = useState("reply");
  const whatsappEnabled = useWhatsAppEnabled(editableLead.account?.id ?? null);
  const { available: whatsappAvailable, loading: whatsappLoading } = useWhatsAppAvailability({
    accountId: editableLead.account?.id ?? null,
    enabled: whatsappEnabled && Boolean(visible) && !isCreate,
  });
  const [channel, setChannel] = useState(
    whatsappEnabled && initialWhatsAppThreadId ? CHANNEL_WHATSAPP : CHANNEL_MAIL,
  );

  useEffect(() => {
    setChannel(whatsappEnabled && initialWhatsAppThreadId ? CHANNEL_WHATSAPP : CHANNEL_MAIL);
  }, [whatsappEnabled, initialWhatsAppThreadId, leadId]);
  const [actionError, setActionError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  const [pendingPersonIds, setPendingPersonIds] = useState([]);
  const [savingCustomFieldValue, setSavingCustomFieldValue] = useState(false);
  const [pendingPayload, setPendingPayload] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [creatingCompany, setCreatingCompany] = useState(false);

  const [companyItems, setCompanyItems] = useState([]);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const companyQueryRef = useRef(0);
  const companyOffsetRef = useRef(0);
  const pinnedCompanyRef = useRef(null);

  const withPinnedCompany = (items) => {
    const pinned = pinnedCompanyRef.current;
    if (!pinned) return items;
    return [pinned, ...items.filter((item) => Number(item.id) !== Number(pinned.id))];
  };

  const companyAccountId = editableLead.account?.id ?? null;

  const fetchCompanyPage = useCallback(async ({ search = "", offset = 0, reset = false }) => {
    const gen = ++companyQueryRef.current;
    if (reset) {
      companyOffsetRef.current = 0;
    }
    setCompanyLoading(true);
    try {
      const { results, count } = await companiesService.list({
        search: search.trim() || undefined,
        ...(companyAccountId != null ? { account_id: companyAccountId } : {}),
        limit: 50,
        offset,
      });
      if (gen !== companyQueryRef.current) return;
      setCompanyItems((prev) => withPinnedCompany(reset ? results : [...prev, ...results]));
      setCompanyTotal(count);
      companyOffsetRef.current = offset + results.length;
    } catch (err) {
      if (gen === companyQueryRef.current) console.error("Error fetching companies:", err);
    } finally {
      if (gen === companyQueryRef.current) setCompanyLoading(false);
    }
  }, [companyAccountId]);

  const loadedCompanyAccountRef = useRef(undefined);

  useEffect(() => {
    if (loadedCompanyAccountRef.current === undefined) {
      loadedCompanyAccountRef.current = companyAccountId;
      return;
    }
    if (loadedCompanyAccountRef.current === companyAccountId) return;

    loadedCompanyAccountRef.current = companyAccountId;
    pinnedCompanyRef.current = null;
    companyOffsetRef.current = 0;
    setCompanyItems([]);
    setCompanyTotal(0);
    setCompanySearch("");
    fetchCompanyPage({ search: "", offset: 0, reset: true });
  }, [companyAccountId, fetchCompanyPage]);

  const handleCompanySearch = useCallback((q) => {
    setCompanySearch(q);
    fetchCompanyPage({ search: q, offset: 0, reset: true });
  }, [fetchCompanyPage]);

  const handleCompanyLoadMore = useCallback(() => {
    if (companyLoading || companyOffsetRef.current >= companyTotal) return;
    fetchCompanyPage({ search: companySearch, offset: companyOffsetRef.current });
  }, [companyLoading, companyTotal, companySearch, fetchCompanyPage]);

  const selectedCompanyId = getCompanyId(editableLead.company);
  const selectedAccountId = editableLead.account?.id ?? null;
  const personRef = editableLead.person_ref || {};
  const recipient = personRef ? buildRecipientFromPersonRef(personRef) : null;
  const leadPersons = buildLeadPersons(
    recipient,
    editableLead.related_persons,
    editableLead.recipients,
  );
  const [previousCompanyId, setPreviousCompanyId] = useState(null);

  const [personCandidates, setPersonCandidates] = useState([]);
  const [personCandidatesTotal, setPersonCandidatesTotal] = useState(0);
  const [personCandidatesLoading, setPersonCandidatesLoading] = useState(false);
  const [personCandidatesSearch, setPersonCandidatesSearch] = useState("");
  const [personCandidatesError, setPersonCandidatesError] = useState(null);
  const personQueryRef = useRef(0);
  const personOffsetRef = useRef(0);
  const personLoadingRef = useRef(false);

  useEffect(() => {
    personQueryRef.current += 1;
    personOffsetRef.current = 0;
    personLoadingRef.current = false;
    setPersonCandidates([]);
    setPersonCandidatesTotal(0);
    setPersonCandidatesSearch("");
    setPersonCandidatesError(null);
    setPersonCandidatesLoading(false);
  }, [selectedAccountId, selectedCompanyId]);

  const fetchPersonCandidatesPage = useCallback(
    async ({ search = "", offset = 0, reset = false }) => {
      const generation = ++personQueryRef.current;

      if (reset) {
        personOffsetRef.current = 0;
        setPersonCandidates([]);
        setPersonCandidatesTotal(0);
      }

      if (!selectedAccountId || !selectedCompanyId) {
        personLoadingRef.current = false;
        setPersonCandidatesLoading(false);
        return;
      }

      personLoadingRef.current = true;
      setPersonCandidatesLoading(true);
      setPersonCandidatesError(null);

      try {
        const { results, count } = await contactsService.list({
          search: search.trim() || undefined,
          limit: PERSON_PAGE_SIZE,
          offset,
          account_id: selectedAccountId,
          company: selectedCompanyId,
        });
        if (generation !== personQueryRef.current) return;

        setPersonCandidates((prev) => (reset ? results : [...prev, ...results]));
        setPersonCandidatesTotal(results.length ? count : offset);
        personOffsetRef.current = offset + results.length;
      } catch (err) {
        if (generation === personQueryRef.current) {
          console.error("Error fetching persons:", err.response?.data || err);
          setPersonCandidatesError(
            extractApiErrorMessage(err) || "No se pudieron cargar las personas.",
          );
        }
      } finally {
        if (generation === personQueryRef.current) {
          personLoadingRef.current = false;
          setPersonCandidatesLoading(false);
        }
      }
    },
    [selectedAccountId, selectedCompanyId],
  );

  const handlePersonPickerOpen = useCallback(() => {
    setPersonCandidatesSearch("");
    fetchPersonCandidatesPage({ search: "", offset: 0, reset: true });
  }, [fetchPersonCandidatesPage]);

  const handlePersonSearch = useCallback(
    (query) => {
      setPersonCandidatesSearch(query);
      fetchPersonCandidatesPage({ search: query, offset: 0, reset: true });
    },
    [fetchPersonCandidatesPage],
  );

  const handlePersonLoadMore = useCallback(() => {
    if (personLoadingRef.current || personCandidatesLoading) return;
    if (personCandidates.length >= personCandidatesTotal) return;
    fetchPersonCandidatesPage({
      search: personCandidatesSearch,
      offset: personOffsetRef.current,
    });
  }, [
    personCandidatesLoading,
    personCandidates.length,
    personCandidatesTotal,
    personCandidatesSearch,
    fetchPersonCandidatesPage,
  ]);

  const uniqueStatuses = useMemo(() => {
    const seenOrders = new Set();

    return (statuses || []).filter((status) => {
      const order = typeof status === "object" ? status.order : status;

      if (seenOrders.has(order)) return false;

      seenOrders.add(order);
      return true;
    });
  }, [statuses]);

  const leadPersonIds = new Set(leadPersons.map((person) => Number(person.id)));
  const personPicker = {
    candidates: personCandidates
      .filter((person) => !leadPersonIds.has(Number(person.id)))
      .map(toPersonOption),
    loading: personCandidatesLoading,
    loadError: personCandidatesError,
    hasMore: personCandidates.length < personCandidatesTotal,
    onOpen: handlePersonPickerOpen,
    onSearch: handlePersonSearch,
    onLoadMore: handlePersonLoadMore,
    emptyMessage: getPersonPickerEmptyMessage({
      accountId: selectedAccountId,
      companyId: selectedCompanyId,
    }),
  };

  const enrichedResponsible = useMemo(() => {
    if (!editableLead.responsible?.id) return editableLead.responsible;
    return (
      (responsible || []).find(
        (r) => Number(r.id) === Number(editableLead.responsible.id),
      ) || editableLead.responsible
    );
  }, [editableLead.responsible, responsible]);

  const getMailCorpAccountId = (mailCorp) =>
    typeof mailCorp?.account === "number"
      ? mailCorp.account
      : mailCorp?.account?.id ?? null;

  const validateRequiredFields = (leadState) => {
    const errors = {};

    if (!String(leadState.lead_name ?? "").trim()) {
      errors.lead_name = "Ingresá un nombre";
    }

    if (!leadState.type?.id) {
      errors.type = "Seleccioná un tipo";
    }

    if (!leadState.account?.id) {
      errors.account = "Seleccioná una cuenta";
    }

    if (!leadState.responsible?.id) {
      errors.responsible = "Seleccioná un responsable";
    }

    if (!getCompanyId(leadState.company)) {
      errors.company = "Seleccioná una compañía";
    }

    if (!leadState.person_ref?.id) {
      errors.person_ref = "Seleccioná un receptor";
    }

    return errors;
  };

  const validateLeadState = (leadState, pendingFields = {}) => {
    const errors = validateRequiredFields(leadState);
    const accountId = leadState.account?.id ?? null;
    if (pendingFields.account && accountId && !leadState.responsible?.id) {
      errors.responsible = "Seleccioná un responsable para la nueva cuenta";
      return errors;
    }
    const mailCorpAccountId = getMailCorpAccountId(leadState.responsible);
    if (accountId && leadState.responsible?.id && mailCorpAccountId) {
      if (Number(mailCorpAccountId) !== Number(accountId)) {
        errors.responsible = "Debe pertenecer a la cuenta seleccionada";
      }
    }
    return errors;
  };

  const filteredResponsibleOptions = useMemo(() => {
    const selectedAccountId = editableLead.account?.id ?? null;
    if (!selectedAccountId) return responsible || [];
    return (responsible || []).filter(
      (mailCorp) => Number(getMailCorpAccountId(mailCorp)) === Number(selectedAccountId),
    );
  }, [responsible, editableLead.account?.id]);

  const visibleLeadCustomFieldValues = Object.fromEntries(
    Object.entries(leadCustomFieldValues || {}).filter(([, item]) => {
      const fieldAccountId =
        item?.field_detail?.account?.id ??
        item?.field_detail?.account_id ??
        item?.field_detail?.account;

      return Number(fieldAccountId) === Number(selectedAccountId);
    }),
  );

  const visibleLeadCustomFields = Object.values(visibleLeadCustomFieldValues)
    .map((item) => item.field_detail)
    .filter(Boolean);

  const availableCustomFields = (customFields || []).filter(
    (field) => !visibleLeadCustomFieldValues?.[field.id],
  );

  useEffect(() => {
    if (lead) {
      const leadState = buildLeadState(lead);

      setEditableLead(leadState);
      setPreviousCompanyId(getCompanyId(leadState.company));
      return;
    }

    if (isCreate) {
      setEditableLead(buildEmptyLead());
      setPreviousCompanyId(null);
    }
  }, [lead, isCreate]);

  useEffect(() => {
    if (!visible || !lead?.id) return;

    let cancelled = false;

    const fetchDetail = async () => {
      try {
        setLoadingDetail(true);
        setDetailError(null);

        const detail = await leadsService.getLeadDetail({ leadId: lead.id });

        if (!cancelled) {
          const detailState = buildLeadState(detail);

          setEditableLead(detailState);
          setPreviousCompanyId(getCompanyId(detailState.company));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching lead detail:", err);
          setDetailError(err);
        }
      } finally {
        if (!cancelled) {
          setLoadingDetail(false);
        }
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [visible, lead?.id]);

  useEffect(() => {
    if (!visible) return;
    if (!editableLead.account?.id) return;

    fetchCustomFieldsByAccount?.(editableLead.account.id);
  }, [visible, editableLead.account?.id, fetchCustomFieldsByAccount]);

  useEffect(() => {
    if (!visible || !editableLead.id) return;

    fetchLeadCustomFieldValues?.(editableLead.id);
  }, [visible, editableLead.id, fetchLeadCustomFieldValues]);

  useEffect(() => {
    if (!visible) return;
    fetchCompanyPage({ search: "", offset: 0, reset: true });
  }, [visible, fetchCompanyPage]);

  useEffect(() => {
    if (!visible) return;
    if (!selectedCompanyId) return;

    const applyResolvedCompany = (company) => {
      setEditableLead((prev) => ({
        ...prev,
        company,
        country: countryFromCompany(company, countries, prev.country),
      }));
    };

    const companyFromList = companyItems.find(
      (company) => Number(company.id) === Number(selectedCompanyId),
    );

    if (companyFromList) {
      applyResolvedCompany(companyFromList);
      return;
    }

    let outdated = false;
    fetchCompanyDetail?.(selectedCompanyId).then((company) => {
      if (outdated || !company) return;
      applyResolvedCompany(company);
    });
    return () => {
      outdated = true;
    };
  }, [visible, selectedCompanyId, companyItems, fetchCompanyDetail, countries]);

  useEffect(() => {
    if (!visible) return;

    if (isCreate && !selectedCompanyId) {
      fetchPersonsByCompany?.();
      return;
    }

    if (!selectedCompanyId) return;

    fetchPersonsByCompany?.(selectedCompanyId);
  }, [visible, isCreate, selectedCompanyId, fetchPersonsByCompany]);

  useEffect(() => {
    if (!visible || !selectedCompanyId) return;

    const companyChanged =
      previousCompanyId &&
      Number(previousCompanyId) !== Number(selectedCompanyId);

    if (!companyChanged) return;

    const currentRecipientId = editableLead.person_ref?.id;

    if (!personsByCompany?.length) {
      setEditableLead((prev) => ({
        ...prev,
        person_ref: null,
      }));

      setPreviousCompanyId(selectedCompanyId);
      return;
    }

    const existsInCompany = personsByCompany.some(
      (person) => Number(person.id) === Number(currentRecipientId),
    );

    if (existsInCompany) {
      setPreviousCompanyId(selectedCompanyId);
      return;
    }

    const firstPerson = personsByCompany[0];

    setEditableLead((prev) => ({
      ...prev,
      person_ref: firstPerson,
    }));

    setPreviousCompanyId(selectedCompanyId);
  }, [
    visible,
    selectedCompanyId,
    previousCompanyId,
    personsByCompany,
    editableLead.person_ref?.id,
  ]);

  useEffect(() => {
    pinnedCompanyRef.current = null;
    setValidationErrors({});
    setPendingPayload({});
    setCreateError(null);
    setActionError(null);
    setPendingPersonIds([]);
    setComposerMode("reply");
  }, [lead?.id, visible]);

  const setPersonPending = (personId, pending) => {
    setPendingPersonIds((prev) => {
      const others = prev.filter((id) => Number(id) !== Number(personId));
      return pending ? [...others, personId] : others;
    });
  };

  const toLeadPerson = (person) => ({
    id: person.id,
    name: person.name || getPersonName(person),
    position: person.position || "",
    email: getActivePersonEmail(person),
    photo: getPersonPhoto(person),
    role: "contacto",
  });

  const persistRelatedPersons = async (nextRelatedPersons) => {
    if (editableLead.id) {
      const updated = await leadsService.updateLead({
        leadId: editableLead.id,
        payload: buildPatchPayload("related_persons", nextRelatedPersons),
      });
      setEditableLead((prev) => ({ ...prev, related_persons: nextRelatedPersons }));
      onSaved?.(updated);
      return;
    }

    setEditableLead((prev) => ({ ...prev, related_persons: nextRelatedPersons }));
  };

  const handleAddLeadPerson = async (person, { asRecipient = false } = {}) => {
    const currentRelatedPersons = editableLead.related_persons || [];
    const currentRecipients = leadPersons.filter(
      (leadPerson) => leadPerson.role === "receptor",
    );
    const isSamePerson = (candidate) => Number(candidate.id) === Number(person.id);
    const alreadyRelated = currentRelatedPersons.some(isSamePerson);
    const alreadyRecipient = currentRecipients.some(isSamePerson);
    const becomesReferent = !editableLead.id && !editableLead.person_ref?.id;
    const makesRecipient = (asRecipient || becomesReferent) && !alreadyRecipient;
    const nextRelatedPersons = alreadyRelated
      ? currentRelatedPersons
      : [...currentRelatedPersons, person];
    const nextRecipients = makesRecipient
      ? [...currentRecipients, person]
      : currentRecipients;

    try {
      if (editableLead.id && (!alreadyRelated || makesRecipient)) {
        const updated = await leadsService.updateLead({
          leadId: editableLead.id,
          payload: {
            ...(alreadyRelated
              ? {}
              : buildPatchPayload("related_persons", nextRelatedPersons)),
            ...(makesRecipient
              ? buildPatchPayload("recipients", nextRecipients)
              : {}),
          },
        });
        onSaved?.(updated);
      }
    } catch (err) {
      console.error("Error updating lead persons:", err.response?.data || err);
      setActionError(
        extractApiErrorMessage(err) || "No se pudo agregar la persona al lead.",
      );
      return null;
    }

    setEditableLead((prev) => ({
      ...prev,
      related_persons: nextRelatedPersons,
      ...(makesRecipient ? { recipients: nextRecipients } : {}),
      ...(becomesReferent ? { person_ref: person } : {}),
    }));

    return {
      ...toLeadPerson(person),
      role: alreadyRecipient || makesRecipient ? "receptor" : "contacto",
    };
  };

  const handleRemoveLeadPerson = async (person) => {
    const nextRelatedPersons = (editableLead.related_persons || []).filter(
      (relatedPerson) => Number(relatedPerson.id) !== Number(person.id),
    );

    setPersonPending(person.id, true);
    try {
      await persistRelatedPersons(nextRelatedPersons);
    } catch (err) {
      console.error("Error updating related_persons:", err.response?.data || err);
      setActionError(
        extractApiErrorMessage(err) || "No se pudo quitar la persona del lead.",
      );
    } finally {
      setPersonPending(person.id, false);
    }
  };

  const handleToggleRecipient = async (person) => {
    const currentRecipients = leadPersons.filter(
      (leadPerson) => leadPerson.role === "receptor",
    );
    const isRecipient = currentRecipients.some(
      (recipientPerson) => Number(recipientPerson.id) === Number(person.id),
    );

    if (isRecipient && currentRecipients.length <= 1) return;
    if (!isRecipient && !person.email) return;

    const nextRecipients = isRecipient
      ? currentRecipients.filter(
          (recipientPerson) => Number(recipientPerson.id) !== Number(person.id),
        )
      : [...currentRecipients, person];
    const keepsPersonRef = nextRecipients.some(
      (recipientPerson) =>
        Number(recipientPerson.id) === Number(editableLead.person_ref?.id),
    );
    const previousPersonRef = editableLead.person_ref;
    const nextPersonRef = keepsPersonRef
      ? previousPersonRef
      : nextRecipients[0] ?? null;
    const personRefChanged =
      Number(nextPersonRef?.id) !== Number(previousPersonRef?.id);
    const currentRelatedPersons = editableLead.related_persons || [];
    const previousPersonRefMissing =
      personRefChanged &&
      Boolean(previousPersonRef?.id) &&
      !currentRelatedPersons.some(
        (relatedPerson) => Number(relatedPerson.id) === Number(previousPersonRef.id),
      );
    const nextRelatedPersons = previousPersonRefMissing
      ? [...currentRelatedPersons, previousPersonRef]
      : currentRelatedPersons;

    const previousState = {
      recipients: editableLead.recipients,
      person_ref: previousPersonRef,
      related_persons: currentRelatedPersons,
    };

    setEditableLead((prev) => ({
      ...prev,
      recipients: nextRecipients,
      person_ref: nextPersonRef,
      related_persons: nextRelatedPersons,
    }));

    if (!editableLead.id) return;

    setPersonPending(person.id, true);
    try {
      const updated = await leadsService.updateLead({
        leadId: editableLead.id,
        payload: {
          ...buildPatchPayload("recipients", nextRecipients),
          ...(previousPersonRefMissing
            ? buildPatchPayload("related_persons", nextRelatedPersons)
            : {}),
        },
      });
      onSaved?.(updated);
    } catch (err) {
      console.error("Error updating recipients:", err.response?.data || err);
      setEditableLead((prev) => ({ ...prev, ...previousState }));
      setActionError(
        extractApiErrorMessage(err) || "No se pudo actualizar el receptor.",
      );
    } finally {
      setPersonPending(person.id, false);
    }
  };

  const handleMarkRecipient = (person) => {
    const isRecipient = leadPersons.some(
      (leadPerson) =>
        leadPerson.role === "receptor" &&
        Number(leadPerson.id) === Number(person.id),
    );

    if (isRecipient) return Promise.resolve();

    return handleToggleRecipient(person);
  };

  if (!visible) return null;

  const handleCreate = async () => {
    const errors = validateRequiredFields(editableLead);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const payload = {
        lead_name: editableLead.lead_name ?? "",
        type: editableLead.type?.id ? { id: editableLead.type.id } : null,
        account: editableLead.account?.id ? { id: editableLead.account.id } : null,
        responsible: editableLead.responsible?.id ? { id: editableLead.responsible.id } : null,
        person_ref: editableLead.person_ref?.id ? { id: editableLead.person_ref.id } : null,
        company: getCompanyId(editableLead.company),
        related_persons: (editableLead.related_persons || []).map((p) => ({ id: p.id })),
        recipients: (editableLead.recipients || []).map((p) => ({ id: p.id })),
      };
      const created = await leadsService.createLead({ payload });
      onSaved?.(created);
      onClose?.();
    } catch (err) {
      console.error("Error creating lead:", err.response?.data || err);
      setCreateError(
        extractApiErrorMessage(err) || "No se pudo crear el lead. Intentá de nuevo.",
      );
    } finally {
      setSaving(false);
    }
  };

  const clearFieldError = (field) => {
    setValidationErrors((previous) => {
      if (!previous[field]) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleFieldChange = (field, value) => {
    setEditableLead((prev) => applyFieldToLead(prev, field, value));
    clearFieldError(field);
  };

  const handleOpenCreateCompany = () => {
    if (!editableLead.account?.id) {
      setValidationErrors((previous) => ({
        ...previous,
        account: "Elegí la cuenta del lead antes de crear la empresa.",
      }));
      return;
    }
    setCreatingCompany(true);
  };

  const handleCompanyCreated = (createdCompany) => {
    setCreatingCompany(false);
    if (!createdCompany?.id) return;
    pinnedCompanyRef.current = createdCompany;
    setCompanyItems((previous) => withPinnedCompany(previous));
    handleFieldChange("company", createdCompany);
    handleFieldCommit("company", createdCompany);
  };

  const handleFieldCommit = async (field, value) => {
    clearFieldError(field);
    if (field === "company") {
      if (isCreate) {
        setEditableLead((prev) => applyFieldToLead(prev, field, value));
        return;
      }
      if (!editableLead.id) return;

      const previousLead = editableLead;
      const newCompanyId = value?.id ?? value;
      setEditableLead((prev) => ({ ...prev, company: value, person_ref: null }));
      setPreviousCompanyId(newCompanyId ?? null);

      const persons = (await fetchPersonsByCompany?.(newCompanyId)) ?? [];
      const firstPerson = persons[0] || null;
      if (firstPerson) setEditableLead((prev) => ({ ...prev, person_ref: firstPerson }));

      const nextCompanyLead = {
        ...previousLead,
        company: value,
        person_ref: firstPerson,
      };
      const companyPayload = {
        ...buildPatchPayload("company", value),
        ...buildPatchPayload("person_ref", firstPerson),
      };
      const newPendingPayload = { ...pendingPayload, ...companyPayload };
      const errors = validateLeadState(nextCompanyLead, newPendingPayload);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        setPendingPayload(newPendingPayload);
        return;
      }

      setPendingPayload({});

      try {
        const updated = await leadsService.updateLead({
          leadId: editableLead.id,
          payload: newPendingPayload,
        });
        onSaved?.(updated);
      } catch (err) {
        console.error("Update lead field error:", err.response?.data || err);
        setEditableLead(previousLead);
        setPendingPayload(pendingPayload);
      }
      return;
    }

    let nextLead = applyFieldToLead(editableLead, field, value);

    if (field === "account") {
      const mailCorpAccountId = getMailCorpAccountId(nextLead.responsible);
      const compatible =
        nextLead.responsible?.id &&
        mailCorpAccountId &&
        Number(mailCorpAccountId) === Number(value?.id);
      if (!compatible) nextLead = { ...nextLead, responsible: null };

      const companyChangedAccount =
        Boolean(getCompanyId(nextLead.company)) &&
        Number(editableLead.account?.id ?? 0) !== Number(value?.id ?? 0);
      if (companyChangedAccount) {
        nextLead = { ...nextLead, company: null };
      }
    }

    if (field === "responsible") {
      const mailCorpAccountId = getMailCorpAccountId(value);
      const matchingAccount = mailCorpAccountId
        ? (accounts || []).find((a) => Number(a.id) === Number(mailCorpAccountId)) ?? null
        : null;
      if (matchingAccount) nextLead = { ...nextLead, account: matchingAccount };
    }

    setEditableLead(nextLead);

    if (isCreate) return;
    if (!editableLead.id) return;

    let fieldPayload = buildPatchPayload(field, value);
    if (field === "responsible" && nextLead.account?.id !== editableLead.account?.id) {
      fieldPayload = { ...fieldPayload, ...buildPatchPayload("account", nextLead.account) };
    }

    const newPendingPayload = { ...pendingPayload, ...fieldPayload };
    const errors = validateLeadState(nextLead, newPendingPayload);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setPendingPayload(newPendingPayload);
      return;
    }

    setPendingPayload({});
    const previousLead = editableLead;

    try {
      const updated = await leadsService.updateLead({
        leadId: editableLead.id,
        payload: newPendingPayload,
      });
      onSaved?.(updated);
    } catch (err) {
      console.error("Update lead field error:", err.response?.data || err);
      setEditableLead(previousLead);
      setPendingPayload(pendingPayload);
    }
  };

  const handleStatusSelect = async (status) => {
    if (pendingStatusId !== null) return;
    if (!status?.id) return;
    const currentStatus = editableLead.status;
    const isCurrentStep =
      status.id === currentStatus?.id ||
      (currentStatus?.order != null && status.order === currentStatus.order);
    if (isCurrentStep) return;
    setPendingStatusId(status.id);
    try {
      await handleFieldCommit("status", status);
    } finally {
      setPendingStatusId(null);
    }
  };

  const handleCustomFieldCommit = async (customField, value) => {
    if (!editableLead.id || !customField?.id) return false;

    const existingValue = visibleLeadCustomFieldValues?.[customField.id];

    setSavingCustomFieldValue(true);

    try {
      if (existingValue?.id) {
        const updated = await customFieldsService.updateCustomFieldValue(
          editableLead.id,
          existingValue.id,
          {
            custom_field: customField.id,
            value: String(value ?? ""),
          },
        );

        const updatedData = updated?.data ?? updated;

        setLeadCustomFieldValues((prev) => ({
          ...prev,
          [customField.id]: {
            ...(prev[customField.id] || {}),
            ...updatedData,
            custom_field: customField.id,
            value: String(value ?? ""),
          },
        }));

        return true;
      }

      const created = await customFieldsService.createCustomFieldValue(
        editableLead.id,
        {
          custom_field: customField.id,
          value: String(value ?? ""),
        },
      );

      const createdData = created?.data ?? created;

      setLeadCustomFieldValues((prev) => ({
        ...prev,
        [customField.id]: createdData,
      }));

      return true;
    } catch (err) {
      console.error(
        "Error saving custom field value:",
        err.response?.data || err,
      );

      return false;
    } finally {
      setSavingCustomFieldValue(false);
    }
  };

  const handleAddCustomFieldValue = async (fieldId, value) => {
    const selectedField = (customFields || []).find(
      (field) => String(field.id) === String(fieldId),
    );

    if (!selectedField) return;

    const saved = await handleCustomFieldCommit(selectedField, value);

    if (saved) setShowCustomFieldForm(false);
  };

  const canDeleteLead =
    !isCreate &&
    (supervisedAccounts ?? []).some(
      (account) => Number(account?.id) === Number(editableLead?.account?.id),
    );

  const handleDeleteLead = async () => {
    setDeletingLead(true);
    try {
      await leadsService.deleteLead({ leadId: editableLead.id });
      setConfirmingDelete(false);
      onDeleted?.(editableLead.id);
      onClose?.();
    } catch (requestError) {
      setActionError(describeRequestError(requestError, "No se pudo eliminar el lead"));
    } finally {
      setDeletingLead(false);
    }
  };

  return (
    <ModalOverlay className={styles.modalOverlay} onClose={onClose}>
      <button
        type="button"
        className={
          isCreate
            ? `${styles.closeButton} ${styles.closeButtonCreate}`
            : styles.closeButton
        }
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        aria-label="Cerrar"
      >
        <CrossIcon className={styles.closeIcon} aria-hidden="true" focusable="false" />
      </button>

      <div
        className={isCreate ? `${styles.modal} ${styles.createModal}` : styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        <ErrorPopup message={actionError} onClose={() => setActionError(null)} />
        {confirmingDelete && (
          <ConfirmDeleteModal
            name={editableLead?.lead_name}
            title="¿Eliminar lead?"
            description={
              <>
                El lead <strong>{editableLead?.lead_name}</strong> dejará de verse en la app y
                se frenarán sus envíos pendientes.
              </>
            }
            deleting={deletingLead}
            onCancel={() => {
              if (!deletingLead) setConfirmingDelete(false);
            }}
            onConfirm={handleDeleteLead}
          />
        )}

        {loadingDetail ? (
          <LoadingScreen embedded mainText="Cargando detalle..." subText="" />
        ) : detailError ? (
          <div style={{ padding: 40, textAlign: "center", color: "red" }}>
            {detailError.response?.status === 403
              ? (detailError.response?.data?.detail || "No tenés permiso para ver este lead.")
              : "Error al cargar detalle"}
          </div>
        ) : (
          <div
            className={
              isCreate
                ? `${styles.leadInfoLayout} ${styles.createLeadLayout}`
                : styles.leadInfoLayout
            }
          >
            <div className={styles.leftColumn}>
              <div className={styles.frameGroup}>
                <LeadStageCard
                  id={editableLead.id || "Nuevo"}
                  name={editableLead.lead_name || "Nuevo Lead"}
                  company={editableLead.account?.name || ""}
                  companyColor={editableLead.account?.color}
                  contactName={recipient?.name || ""}
                  contactRole={personRef.position || ""}
                  onDelete={
                    canDeleteLead ? () => setConfirmingDelete(true) : null
                  }
                />


                <LeadStatusProgress
                  statuses={uniqueStatuses}
                  currentStatusOrder={editableLead?.status?.order ?? null}
                  onStatusSelect={isCreate ? null : handleStatusSelect}
                  pendingStatusId={pendingStatusId}
                />
              </div>

              <div className={styles.leadInfoScroll}>
                <LeadDetailsCard
                  name={editableLead.lead_name || ""}
                  company={
                    typeof editableLead.company === "object"
                      ? editableLead.company
                      : null
                  }
                  companyOptions={companyItems}
                  companyLoading={companyLoading}
                  onCompanySearch={handleCompanySearch}
                  onCompanyLoadMore={handleCompanyLoadMore}
                  type={editableLead.type || null}
                  typeOptions={types || []}
                  responsible={enrichedResponsible || null}
                  responsibleOptions={filteredResponsibleOptions}

                  account={editableLead.account || null}
                  accountName={editableLead.account?.name || ""}
                  accountColor={editableLead.account?.color || "#1A5869"}
                  accountOptions={accounts || []}

                  errors={validationErrors}

                  country={selectedCompanyId ? editableLead.country || null : null}
                  countryOptions={countries || []}

                  persons={leadPersons}
                  personPicker={personPicker}
                  onAddPerson={handleAddLeadPerson}
                  onRemovePerson={handleRemoveLeadPerson}
                  onToggleRecipient={handleToggleRecipient}
                  pendingPersonIds={pendingPersonIds}

                  customFields={visibleLeadCustomFields}
                  customFieldValues={visibleLeadCustomFieldValues}
                  onCustomFieldCommit={handleCustomFieldCommit}

                    addCustomFieldSelector={
                      showCustomFieldForm ? (
                        <AddCustomFieldValueForm
                          fields={availableCustomFields}
                          saving={
                            savingCustomFieldValue ||
                            customFieldsLoading ||
                            leadCustomFieldValuesLoading
                          }
                          onAdd={handleAddCustomFieldValue}
                          onCancel={() => setShowCustomFieldForm(false)}
                        />
                      ) : null
                    }

                    onAddData={() => setShowCustomFieldForm(true)}
                    onCreateCompany={handleOpenCreateCompany}
                    onFieldChange={handleFieldChange}
                    onFieldCommit={handleFieldCommit}
                />
                {isCreate && (
                  <div className={styles.createFooter}>
                    {createError && (
                      <p className={styles.createError} role="alert">
                        {createError}
                      </p>
                    )}
                    <button
                      className={styles.saveButton}
                      onClick={handleCreate}
                      disabled={saving}
                    >
                      {saving ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                )}
              </div>

                {!isCreate && (
                  <div className={styles.leadSummarySlot}>
                    <ConversationSummaryPanel
                      leadId={editableLead.id}
                      templateGroupId={
                        selectedCommunicationSubject?.template_group_id ?? null
                      }
                      subjectGroupId={
                        selectedCommunicationSubject?.subject_group_id ??
                        selectedCommunicationSubject?.id ??
                        null
                      }
                    />
                  </div>
                )}
            </div>

            {!isCreate && channel === CHANNEL_WHATSAPP && (
              <LeadWhatsAppPanels
                numberAvailable={whatsappAvailable}
                availabilityLoading={whatsappLoading}
                leadId={editableLead.id}
                accountId={editableLead.account?.id ?? null}
                responsibleMailAccountId={editableLead.responsible?.id ?? null}
                initialThreadId={initialWhatsAppThreadId}
                columnClassNames={{
                  middle: styles.middleColumn,
                  right: styles.rightColumn,
                }}
                tabs={<LeadChannelTabs channel={channel} onChange={setChannel} />}
              />
            )}

            {!isCreate && channel !== CHANNEL_WHATSAPP && (
              <>
                <div className={styles.middleColumn}>
                  {whatsappEnabled && <LeadChannelTabs channel={channel} onChange={setChannel} />}
                  <LeadCommunicationsPanel
                    withChannelTabs={whatsappEnabled}
                    leadId={editableLead.id}
                    recipient={recipient}
                    initialSubjectGroupId={initialSubjectGroupId}
                    onNewMail={() => setComposerMode("new")}
                    onThreadSelected={() => setComposerMode("reply")}
                    onUnreadChange={handleUnreadChange}
                  />
                </div>

                <div className={styles.rightColumn}>
                  <CommunicationDetailPanel
                    recipient={recipient}
                    leadId={editableLead.id}
                    leadPersons={leadPersons}
                    personPicker={personPicker}
                    onAddPerson={handleAddLeadPerson}
                    onMarkRecipient={handleMarkRecipient}
                    accountId={editableLead.account?.id}
                    leadAccount={editableLead.account || null}
                    leadCompany={
                      typeof editableLead.company === "object"
                        ? editableLead.company
                        : null
                    }
                    defaultSenderAccount={enrichedResponsible || editableLead.responsible || null}
                    composerMode={composerMode}
                    onComposerModeChange={setComposerMode}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {creatingCompany &&
        createPortal(
          <div className={styles.companyModalLayer} onClick={(event) => event.stopPropagation()}>
            <CompanyDetailModal
              company={null}
              createMode
              accountId={editableLead.account?.id ?? null}
              onClose={() => setCreatingCompany(false)}
              onSaved={handleCompanyCreated}
            />
          </div>,
          document.body,
        )}
    </ModalOverlay>
  );
};

export default LeadInfoModal;
